import { useState, useEffect, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell,
} from 'recharts';
import client from '../../api/client';

const MONTHLY_LIMIT = 100_000;

const SPORT_LABELS = {
  americanfootball_nfl: 'NFL', americanfootball_ncaaf: 'NCAAF',
  basketball_nba: 'NBA', basketball_ncaab: 'NCAAB',
  baseball_mlb: 'MLB', icehockey_nhl: 'NHL', soccer_usa_mls: 'MLS',
  mma_mixed_martial_arts: 'MMA', boxing_boxing: 'Boxing', golf: 'Golf',
};

const BAR_COLORS = ['#005BB3', '#3b82f6', '#029456', '#F86A38', '#DC2626', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#22d3ee'];

function StatCard({ label, value, sub, color }) {
  return (
    <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-3 py-2.5">
      <p className="text-[12px] uppercase tracking-[0.5px] text-[#6B7280] dark:text-[#8892a8] mb-1" style={{ fontFamily: 'var(--font-sans)' }}>{label}</p>
      <p className={`text-[22px] font-semibold ${color || 'text-[#111827] dark:text-[#e2e8f0]'}`} style={{ fontFamily: 'var(--font-sans)' }}>{value}</p>
      {sub && <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] mt-0.5" style={{ fontFamily: 'var(--font-sans)' }}>{sub}</p>}
    </div>
  );
}

function QuotaBar({ pctUsed }) {
  const barColor = pctUsed >= 90 ? '#DC2626' : pctUsed >= 80 ? '#F86A38' : '#005BB3';
  return (
    <div className="h-3 rounded-full bg-[#E5E7EB] dark:bg-[#1a2030] overflow-hidden">
      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(pctUsed, 100)}%`, background: barColor }} />
    </div>
  );
}

export default function QuotaDashboard() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [bySport, setBySport] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  function fetchAll() {
    setLoading(true); setError(null);
    Promise.all([
      client.get('/api/quota/current'),
      client.get('/api/quota/history?days=30'),
      client.get('/api/quota/by-sport?days=30'),
    ])
      .then(([c, h, s]) => {
        const d = c.data || {};
        setCurrent({
          credits_used: d.credits_used ?? 0,
          credits_remaining: d.credits_remaining ?? MONTHLY_LIMIT,
          pct_used: d.pct_used ?? 0,
          monthly_limit: d.monthly_limit ?? MONTHLY_LIMIT,
          last_updated: d.last_updated ?? null,
        });
        setHistory(h.data.snapshots || []);
        setBySport(s.data.by_sport || []);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }

  useEffect(() => { fetchAll(); }, []);

  // Aggregate history into daily data points
  const dailyChart = useMemo(() => {
    const byDay = {};
    for (const snap of history) {
      const day = snap.created_at.slice(0, 10);
      byDay[day] = { date: day, used: snap.credits_used, remaining: snap.credits_remaining };
    }
    return Object.values(byDay).sort((a, b) => a.date.localeCompare(b.date));
  }, [history]);

  // Depletion projection
  const projection = useMemo(() => {
    if (!current || dailyChart.length < 2) return null;
    const first = dailyChart[0];
    const last = dailyChart[dailyChart.length - 1];
    const daysSpan = Math.max(1, (new Date(last.date) - new Date(first.date)) / (1000 * 60 * 60 * 24));
    const creditsConsumed = last.used - first.used;
    if (creditsConsumed <= 0) return { dailyRate: 0, daysLeft: null, safe: true };

    const dailyRate = Math.round(creditsConsumed / daysSpan);
    const remaining = current.credits_remaining;
    const daysLeft = dailyRate > 0 ? Math.floor(remaining / dailyRate) : null;

    // Days until monthly reset (assume 1st of next month)
    const now = new Date();
    const nextReset = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const daysToReset = Math.ceil((nextReset - now) / (1000 * 60 * 60 * 24));

    const safe = daysLeft === null || daysLeft >= daysToReset;

    return { dailyRate, daysLeft, daysToReset, safe };
  }, [current, dailyChart]);

  // Sport chart data
  const sportChart = useMemo(() =>
    bySport.map((s) => ({
      sport: SPORT_LABELS[s.sport] || s.sport,
      calls: s.calls,
    })),
    [bySport],
  );

  const pctUsed = current?.pct_used || 0;
  const warningLevel = pctUsed >= 90 ? 'critical' : pctUsed >= 80 ? 'warning' : 'ok';

  return (
    <div className="px-5 py-6">
      {/* Header */}
      <div className="mb-1">
        <p className="text-[12px] uppercase tracking-[1px] font-medium text-[#005BB3] dark:text-[#3b82f6]" style={{ fontFamily: 'var(--font-sans)' }}>Admin</p>
      </div>
      <h1 className="text-[22px] font-semibold text-[#111827] dark:text-[#e2e8f0] tracking-tight mb-1" style={{ fontFamily: 'var(--font-sans)' }}>API Quota Dashboard</h1>
      <p className="text-[14px] text-[#6B7280] dark:text-[#8892a8] mb-5" style={{ fontFamily: 'var(--font-sans)' }}>Monitor Odds API credit usage and projected depletion. 100K credits/month.</p>

      {error && (
        <div className="rounded bg-[rgba(239,68,68,0.06)] dark:bg-[rgba(239,68,68,0.08)] border border-[#fecaca] dark:border-[#7f1d1d] px-3 py-2 mb-4">
          <p className="text-[#ef4444] text-[11px]">Failed to load: {error}</p>
          <button onClick={fetchAll} className="text-[10px] text-[#ef4444] underline mt-1">Retry</button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">{[...Array(4)].map((_, i) => <div key={i} className="h-16 rounded bg-[#F8F9FA] dark:bg-[#151a25] skeleton" />)}</div>
      ) : current && (
        <div className="max-w-[1100px]">
          {/* Warning banner */}
          {warningLevel !== 'ok' && (
            <div className={`rounded-lg border-l-[3px] px-4 py-3 mb-4 ${
              warningLevel === 'critical'
                ? 'border-l-[#DC2626] border border-[#fecaca] dark:border-[#7f1d1d] bg-[rgba(220,38,38,0.04)] dark:bg-[rgba(220,38,38,0.06)]'
                : 'border-l-[#F86A38] border border-[#fed7aa] dark:border-[#78350f] bg-[rgba(248,106,56,0.04)] dark:bg-[rgba(248,106,56,0.06)]'
            }`}>
              <p className={`text-[13px] font-semibold ${warningLevel === 'critical' ? 'text-[#DC2626]' : 'text-[#F86A38]'}`}>
                {warningLevel === 'critical' ? 'Critical: ' : 'Warning: '}
                {pctUsed.toFixed(0)}% of monthly quota used — {current.credits_remaining.toLocaleString()} credits remaining
              </p>
              {projection && !projection.safe && (
                <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8] mt-0.5">
                  At current rate ({projection.dailyRate.toLocaleString()}/day), credits will run out in ~{projection.daysLeft} days. Monthly reset in {projection.daysToReset} days.
                </p>
              )}
            </div>
          )}

          {/* Stat cards */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-4">
            <StatCard label="Credits Used" value={current.credits_used.toLocaleString()} sub={`of ${MONTHLY_LIMIT.toLocaleString()}`} />
            <StatCard label="Remaining" value={current.credits_remaining.toLocaleString()} color={warningLevel === 'critical' ? 'text-[#DC2626]' : warningLevel === 'warning' ? 'text-[#F86A38]' : 'text-[#029456]'} />
            <StatCard label="% Used" value={`${pctUsed.toFixed(1)}%`} />
            <StatCard
              label="Daily Burn Rate"
              value={projection?.dailyRate != null ? projection.dailyRate.toLocaleString() : '--'}
              sub="credits/day avg"
            />
            <StatCard
              label="Projected"
              value={projection?.daysLeft != null ? `${projection.daysLeft}d left` : 'Stable'}
              color={projection && !projection.safe ? 'text-[#DC2626]' : 'text-[#029456]'}
              sub={projection ? `Reset in ${projection.daysToReset}d` : null}
            />
          </div>

          {/* Quota bar */}
          <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3 mb-4">
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[12px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Monthly Quota</span>
              <span className="text-[12px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>
                {current.credits_used.toLocaleString()} / {MONTHLY_LIMIT.toLocaleString()}
              </span>
            </div>
            <QuotaBar pctUsed={pctUsed} />
          </div>

          {/* Charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4 mb-4">
            {/* Usage over time */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
                <h3 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Usage Over Time</h3>
              </div>
              {dailyChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <AreaChart data={dailyChart}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                    <XAxis dataKey="date" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent"
                      tickFormatter={(v) => { const d = new Date(v + 'T00:00:00'); return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }); }}
                    />
                    <YAxis tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent" />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 4, color: 'var(--tooltip-text)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                      formatter={(v) => [v.toLocaleString(), 'Credits Used']}
                      labelFormatter={(v) => new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    />
                    <Area type="monotone" dataKey="used" stroke="#005BB3" fill="rgba(0,91,179,0.1)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#6B7280] dark:text-[#8892a8] text-[13px] py-8 text-center">No usage data yet. Data appears after Odds API calls are made.</p>
              )}
            </div>

            {/* By sport */}
            <div className="rounded-md border border-[#E5E7EB] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-4 py-3">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-[3px] h-4 bg-[#005BB3] rounded-full" />
                <h3 className="text-[12px] uppercase tracking-[0.5px] font-semibold text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-sans)' }}>Usage by Sport</h3>
              </div>
              {sportChart.length > 0 ? (
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={sportChart} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
                    <XAxis type="number" tick={{ fill: '#6B7280', fontSize: 10, fontFamily: 'var(--font-mono)' }} stroke="transparent" />
                    <YAxis type="category" dataKey="sport" tick={{ fill: '#6B7280', fontSize: 11, fontFamily: 'var(--font-sans)' }} stroke="transparent" width={60} />
                    <Tooltip
                      contentStyle={{ backgroundColor: 'var(--tooltip-bg)', border: '1px solid var(--tooltip-border)', borderRadius: 4, color: 'var(--tooltip-text)', fontSize: 11, fontFamily: 'var(--font-mono)' }}
                      formatter={(v) => [v.toLocaleString(), 'API Calls']}
                    />
                    <Bar dataKey="calls" radius={[0, 4, 4, 0]}>
                      {sportChart.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-[#6B7280] dark:text-[#8892a8] text-[13px] py-8 text-center">No sport data yet.</p>
              )}
            </div>
          </div>

          {/* Last updated */}
          {current.last_updated && (
            <p className="text-[12px] text-[#6B7280] dark:text-[#8892a8]" style={{ fontFamily: 'var(--font-mono)' }}>
              Last updated: {new Date(current.last_updated).toLocaleString()}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
